import { ReactNode } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import { BUSAN_CENTER, BUSAN_ZOOM, ORIGIN_COORDS, TILE_LAYERS, originIcon } from "@/lib/map";
import { useOrgStore } from "@/store/org";

interface MapViewProps {
  center?: [number, number];
  zoom?: number;
  height?: string;
  tile?: keyof typeof TILE_LAYERS;
  showOrigin?: boolean;
  origin?: [number, number];
  children?: ReactNode;
}

function FitBoundsOnData({ points }: { points: [number, number][] }) {
  const map = useMap();
  if (points.length > 0) {
    try {
      const bounds = L.latLngBounds(points.map((p) => L.latLng(p[0], p[1])));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    } catch {}
  }
  return null;
}

export default function MapView({
  center,
  zoom = BUSAN_ZOOM,
  height = "calc(100vh - 220px)",
  tile = "osm", // 카르토 무료 타일이 2026-09부터 API 키를 요구해 "API KEY REQUIRED" 격자가 깔렸다(3차 예행연습 1번) — 키 없는 오픈스트리트맵으로
  showOrigin = true,
  origin,
  children,
}: MapViewProps) {
  const t = TILE_LAYERS[tile];
  // org 창고 지도 위치(orgs 문서) 우선 — 미설정 시 기존 기본값(org#1 부산) 폴백
  const wh = useOrgStore((s) => s.org?.warehouse);
  const effCenter: [number, number] = center ?? (wh ? [wh.lat, wh.lng] : BUSAN_CENTER);
  const effOrigin: [number, number] = origin ?? (wh ? [wh.lat, wh.lng] : ORIGIN_COORDS);
  const originLabel = wh?.label || "물류 출발지";
  return (
    <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm" style={{ height }}>
      <MapContainer
        center={effCenter}
        zoom={zoom}
        scrollWheelZoom
        style={{ width: "100%", height: "100%" }}
      >
        <TileLayer url={t.url} attribution={t.attribution} maxZoom={t.maxZoom} />
        {showOrigin && (
          <Marker position={effOrigin} icon={originIcon()}>
            <Popup>{originLabel}<br />(우유 배송 출발지)</Popup>
          </Marker>
        )}
        {children}
      </MapContainer>
    </div>
  );
}

export { MarkerClusterGroup, Marker, Popup, Polyline, FitBoundsOnData };
